// New party form — manually create a customer / counterparty / own_account.
//
// Lives at /parties/new. Mirrors the lazy-create flow's choices, but lets
// the operator onboard a party without going through a transaction.
//
// own_account uses structured pickers (kind / provider / label) — the slug
// gets built automatically so the operator never has to think about
// dot-separated hierarchies.

import { useState, useMemo, useEffect } from 'react';
import { errorWords, readWords } from '../lib/errors';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { tg } from '../lib/telegram';
import { MoreDetails } from '../components/MoreDetails';
import { Combobox, type ComboboxOption } from '../components/Combobox';
import { Chip } from '../components/FilterRail';
import { Field } from '../components/Field';
import { ScreenHeader } from '../components/ScreenHeader';
import { ErrorLine } from '../components/ErrorLine';
import { StateLine } from '../components/kit';
import { partyName, partyTypeLabel, trustTierLabel } from '../lib/naming';
import { ASSETS, isFiat } from '../lib/workflow';
import { displayAsset } from '../lib/format';
import { readState } from '../lib/read-state';

type PartyType = 'customer' | 'counterparty' | 'own_account';
type ProfileKind = 'tracked' | 'treasury_only';
type CounterpartyRole = 'merchant' | 'other';
type TrustTier = 'new' | 'standard' | 'repeat' | 'vip';
type RailKind = 'bank' | 'exchange' | 'hot' | 'cold' | 'cash' | 'angadiya';

const COUNTERPARTY_ROLE_LABELS: Record<CounterpartyRole, string> = {
  merchant: 'Merchant',
  other:    'Other',
};

const RAIL_KIND_LABELS: Record<RailKind, string> = {
  bank:     'Bank',
  exchange: 'Exchange',
  hot:      'Hot wallet',
  cold:     'Cold wallet',
  cash:     'Cash',
  angadiya: 'Angadiya',
};

// What a merchant can do for the desk, in words. The stored token is what the
// BFF wants; the operator picking it should never have to read 'mc'.
const CAPABILITY_LABELS: Record<string, string> = {
  'mc':            "Manager's cheque",
  'passthrough':   'Passthrough',
  'bank-transfer': 'Bank transfer',
  'cash':          'Cash',
  'crypto':        'Crypto',
};
const CAPABILITIES = ['mc', 'passthrough', 'bank-transfer', 'cash', 'crypto'] as const;

// WHAT A CASH OR BANK ACCOUNT MAY HOLD (14 Sept 2026). A desk account is
// opened here and nowhere else (the rail picker's manual door is gone), and
// the books refuse to open an asset on a rail from a posting — so the form
// has to say which currencies the account holds, or the first euro into it
// meets "has never held EUR" on its very first receipt. The choice becomes
// admin.rail_assets rows, written with the party, and every picker offers
// the pairs at zero from then on (registry §3 "Fiat rails and what they may
// hold"). Wallets are not asked: their coins come from the chain
// (admin.wallet_chain_assets), as before.
const FIAT_RAIL_KINDS: ReadonlySet<RailKind> = new Set(['bank', 'cash', 'angadiya']);
const FIATS: readonly string[] = ASSETS.filter((a) => isFiat(a));
/** The one currency such an account usually holds — the first chip lit. */
const usualFiatFor = (kind: RailKind): string => (kind === 'angadiya' ? 'INR' : 'AED');

// Slug-safe transform: lowercase, dashes, strip everything else.
function safeSlug(s: string): string {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 30);
}

// Auto-suffix on collision: short random 4-char tail so two "Walk-in May"
// customers don't collide on slug. Operator never sees the suffix.
function withCollisionSuffix(base: string): string {
  const suffix = Math.random().toString(36).slice(2, 6);
  return `${base}-${suffix}`;
}

export function NewParty() {
  const nav = useNavigate();
  const qc  = useQueryClient();

  const [partyType, setPartyType] = useState<PartyType>('customer');
  const [notes, setNotes]         = useState('');

  // ── Customer fields ────────────────────────────────────────────────────
  const [custDisplayName, setCustDisplayName]   = useState('');
  const [profileKind, setProfileKind]           = useState<ProfileKind>('tracked');
  const [telegramUsername, setTelegramUsername] = useState('');
  const [trustTier, setTrustTier]               = useState<TrustTier>('new');

  // ── Counterparty fields ────────────────────────────────────────────────
  const [cpDisplayName, setCpDisplayName] = useState('');
  const [counterpartyRole, setCounterpartyRole] = useState<CounterpartyRole>('merchant');
  const [capabilities, setCapabilities]   = useState<string[]>([]);

  // ── Own_account fields ─────────────────────────────────────────────────
  // Sub-type: "rail" = specific account (Kind+Provider+Label),
  //           "group" = parent cluster (just a name, like "Anil" / "Bhargav").
  const [ownSubtype, setOwnSubtype]   = useState<'rail' | 'group'>('rail');
  const [ownKind, setOwnKind]         = useState<RailKind>('bank');
  const [ownProvider, setOwnProvider] = useState('');
  const [ownLabel, setOwnLabel]       = useState('');
  const [groupName, setGroupName]     = useState('');
  const [parentSlug, setParentSlug]   = useState('');
  // "May hold" — the currencies of a cash / bank / angadiya account (see
  // FIAT_RAIL_KINDS). Starts on the kind's usual one; a change of kind starts
  // it again, since a bank's AED is not an angadiya's INR.
  const [ownAssets, setOwnAssets]     = useState<string[]>([usualFiatFor('bank')]);
  const pickKind = (k: RailKind) => { setOwnKind(k); setOwnAssets([usualFiatFor(k)]); };
  const fiatRail = ownSubtype === 'rail' && FIAT_RAIL_KINDS.has(ownKind);

  // Auto-derived from kind/provider/label for own_account:
  const ownSlugAuto = useMemo(() => {
    const p = safeSlug(ownProvider);
    const l = safeSlug(ownLabel);
    if (!p) return '';
    return [ownKind, p, l].filter(Boolean).join('.');
  }, [ownKind, ownProvider, ownLabel]);
  const ownDisplayAuto = useMemo(() => {
    const parts = [ownProvider.trim(), ownLabel.trim()].filter(Boolean);
    return parts.length ? parts.join(' · ') : '';
  }, [ownProvider, ownLabel]);

  // Parent picker: only shows GROUPS (own_account rows with treasury_slug NULL).
  // Operator creates groups via the "Group" sub-type below; rails reference one.
  const existingGroups = useQuery({
    queryKey: ['parties', 'groups'],
    queryFn:  () => api.listParties({ entity: 'dubaiotc', is_group: true }),
    enabled:  partyType === 'own_account' && ownSubtype === 'rail',
  });
  // What the group list is worth — `readState` (lib/read-state.ts): "No such group" / "No groups yet" are said only from a read that SUCCEEDED.
  const groups = readState(existingGroups);
  const parentOptions: ComboboxOption[] = useMemo(() =>
    (existingGroups.data?.parties ?? []).slice(0, 30).map((p) => ({
      value: p.slug, label: partyName(p),
    }))
  , [existingGroups.data]);

  // ── Submission ─────────────────────────────────────────────────────────
  // Slug never visible to operator. We build it from the display name,
  // appending a 4-char random suffix so two "Walk-in May" entries don't
  // collide. For own_account the slug doubles as treasury_slug — same shape.
  const submit = useMutation({
    mutationFn: () => {
      const common = { entity: 'dubaiotc' as const, notes: notes || null };

      if (partyType === 'customer') {
        return api.createParty({
          ...common,
          party_type:        'customer',
          slug:              withCollisionSuffix(safeSlug(custDisplayName)),
          display_name:      custDisplayName.trim(),
          profile_kind:      profileKind,
          telegram_username: telegramUsername || null,
          trust_tier:        trustTier,
        });
      }
      if (partyType === 'counterparty') {
        return api.createParty({
          ...common,
          party_type:                'counterparty',
          slug:                      withCollisionSuffix(safeSlug(cpDisplayName)),
          display_name:              cpDisplayName.trim(),
          counterparty_role:         counterpartyRole,
          counterparty_capabilities: capabilities,
        });
      }
      // own_account — two flavours:
      //   "group" = parent cluster (no treasury_slug; just a name)
      //   "rail"  = specific account (treasury_slug from kind.provider.label)
      if (ownSubtype === 'group') {
        return api.createParty({
          ...common,
          party_type:    'own_account',
          slug:          withCollisionSuffix(safeSlug(groupName)),
          display_name:  groupName.trim(),
          treasury_slug: null,        // null treasury_slug ⇒ group
        });
      }
      return api.createParty({
        ...common,
        party_type:        'own_account',
        slug:              ownSlugAuto,
        display_name:      (ownDisplayAuto || ownSlugAuto).trim(),
        treasury_slug:     ownSlugAuto || null,
        parent_party_slug: parentSlug || null,
        // The currencies this account may hold — registered with it, in the
        // same transaction (bff routes/parties.ts). Wallets send nothing.
        assets:            fiatRail ? ownAssets : undefined,
      });
    },
    onSuccess: (data) => {
      tg()?.HapticFeedback.notificationOccurred('success');
      qc.invalidateQueries({ queryKey: ['parties'] });
      nav(`/parties/${encodeURIComponent(data.party.slug)}?entity=dubaiotc`);
    },
    onError: () => tg()?.HapticFeedback.notificationOccurred('error'),
  });

  const validSlugRe = /^[a-z0-9][a-z0-9._-]{1,63}$/;
  const canSubmit = !submit.isPending && (() => {
    if (partyType === 'customer')     return !!custDisplayName.trim() && safeSlug(custDisplayName).length > 0;
    if (partyType === 'counterparty') return !!cpDisplayName.trim()   && safeSlug(cpDisplayName).length   > 0;
    if (ownSubtype === 'group')       return !!groupName.trim()       && safeSlug(groupName).length       > 0;
    // A cash or bank account holds at least one currency, or it can never be
    // posted to (the books refuse to open an asset on a rail from a posting).
    if (fiatRail && ownAssets.length === 0) return false;
    return !!ownProvider.trim() && validSlugRe.test(ownSlugAuto);
  })();

  // The one toggle (styles.css, emphasis rule): the picked mode is lit. A
  // segment's word never wraps — three across on a 375px phone.
  const segmented = (active: boolean) => `seg whitespace-nowrap ${active ? 'seg-on' : ''}`;

  return (
    // THE PHONE PASS (19 Sept 2026): the same column every entry form takes —
    // full-width boxes stacked below lg, a 720px column at lg+ (Book trade,
    // Record money), nothing wider than the screen.
    <div className="space-y-3 lg:max-w-[720px] lg:mx-auto lg:space-y-4">
      <button onClick={() => nav(-1)} className="btn-link text-meta">← Back</button>
      <ScreenHeader title="New party" />

      {/* Type toggle — the three segments share the line on the phone; at lg+
          they sit flat inside the bordered group every mode toggle uses. */}
      <div className="flex gap-2 lg:inline-flex lg:w-auto lg:gap-1 lg:p-1 lg:rounded-lg lg:border lg:border-stroke lg:bg-panel">
        {/* LABELS FROM THE SHARED HELPER. This toggle said "Counterparty" while
            Parties lists them as "Merchants" and the party page's Identity row
            says "Merchant" — three words for one thing, on the screen that
            creates it. */}
        {(['customer', 'counterparty', 'own_account'] as const).map((v) => (
          <button
            key={v}
            onClick={() => setPartyType(v)}
            className={segmented(partyType === v)}
          >
            {partyTypeLabel(v)}
          </button>
        ))}
      </div>

      {/* ── Customer ─────────────────────────────────────────────────── */}
      {partyType === 'customer' && (
        <>
          <Field label="Name">
            <input className="input" value={custDisplayName} onChange={(e) => setCustDisplayName(e.target.value)} placeholder="Acme Realty LLC / Walk-in May" />
          </Field>
          <Field label="Profile">
            <div className="flex gap-2">
              {([
                { v: 'tracked',       l: 'Tracked',       d: 'KYC required'    },
                { v: 'treasury_only', l: 'Treasury-only', d: 'No KYC; name only' },
              ] as const).map((k) => (
                <button
                  key={k.v}
                  onClick={() => setProfileKind(k.v)}
                  className={segmented(profileKind === k.v)}
                  title={k.d}
                >
                  {k.l}
                </button>
              ))}
            </div>
          </Field>
          <MoreDetails label="Telegram, tier">
            <Field label="Telegram username">
              <input className="input" value={telegramUsername} onChange={(e) => setTelegramUsername(e.target.value)} placeholder="@username" />
            </Field>
            <Field label="Trust tier">
              <select className="select" value={trustTier} onChange={(e) => setTrustTier(e.target.value as TrustTier)}>
                {(['new', 'standard', 'repeat', 'vip'] as const).map((t) => (
                  <option key={t} value={t}>{trustTierLabel(t)}</option>
                ))}
              </select>
            </Field>
          </MoreDetails>
        </>
      )}

      {/* ── Counterparty ─────────────────────────────────────────────── */}
      {partyType === 'counterparty' && (
        <>
          <Field label="Name">
            <input className="input" value={cpDisplayName} onChange={(e) => setCpDisplayName(e.target.value)} placeholder="Lucky / HK Desk / PR Angadiya" />
          </Field>
          <Field label="Role">
            <select className="select" value={counterpartyRole} onChange={(e) => setCounterpartyRole(e.target.value as CounterpartyRole)}>
              {(Object.entries(COUNTERPARTY_ROLE_LABELS) as Array<[CounterpartyRole, string]>).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </Field>
          {/* The fold already says "Capabilities" — the chips under it need no second label. */}
          <MoreDetails label="Capabilities">
            <div className="flex flex-wrap gap-1.5">
              {CAPABILITIES.map((cap) => {
                const on = capabilities.includes(cap);
                return (
                  <button
                    key={cap}
                    onClick={() => setCapabilities(on ? capabilities.filter((c) => c !== cap) : [...capabilities, cap])}
                    className={`chip ${on ? 'chip-on' : ''}`}
                  >
                    {CAPABILITY_LABELS[cap] ?? cap}
                  </button>
                );
              })}
            </div>
          </MoreDetails>
        </>
      )}

      {/* ── Own account (account vs group sub-toggle) ─────────────────── */}
      {partyType === 'own_account' && (
        <>
          {/* Account or group — a mode toggle: two halves on the phone, the
              bordered group at lg+, like the type toggle above it. */}
          <div className="flex gap-2 lg:inline-flex lg:w-auto lg:gap-1 lg:p-1 lg:rounded-lg lg:border lg:border-stroke lg:bg-panel">
            {([
              { v: 'rail',  l: 'Account', d: 'One account (a bank account, hot wallet, cash drawer)' },
              { v: 'group', l: 'Group',   d: 'A parent (Anil / Bhargav / Sai) — groups accounts under a person or family' },
            ] as const).map((t) => (
              <button
                key={t.v}
                onClick={() => setOwnSubtype(t.v)}
                className={segmented(ownSubtype === t.v)}
                title={t.d}
              >
                {t.l}
              </button>
            ))}
          </div>
        </>
      )}

      {/* ── Group sub-form: just a name ──────────────────────────────── */}
      {partyType === 'own_account' && ownSubtype === 'group' && (
        <>
          <Field label="Group name">
            <input
              className="input"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="Anil / Bhargav / Sai / Padmavati"
            />
          </Field>
          {/* What a group is, in one line; "set it as Parent" is the Parent field's own job on an account. */}
          <p className="text-meta text-tg-hint">A group only parents accounts — it holds no balance.</p>
        </>
      )}

      {/* ── Account sub-form: structured kind/provider/label ─────────── */}
      {partyType === 'own_account' && ownSubtype === 'rail' && (
        <>
          <Field label="Kind">
            {/* Six kinds, three across, two rows — 110px a cell at 375px, so
                "Cold wallet" fits at the segment's own phone size (14px);
                lg+ keeps the 12px it always had. */}
            <div className="grid grid-cols-3 gap-2">
              {(Object.keys(RAIL_KIND_LABELS) as RailKind[]).map((k) => (
                <button
                  key={k}
                  onClick={() => pickKind(k)}
                  className={`seg whitespace-nowrap lg:text-meta ${ownKind === k ? 'seg-on' : ''}`}
                >
                  {RAIL_KIND_LABELS[k]}
                </button>
              ))}
            </div>
          </Field>

          {/* The currencies this account may hold — chips that toggle, at
              least one lit. Cash, bank and angadiya only; a wallet's coins
              come from its chain (see FIAT_RAIL_KINDS). */}
          {fiatRail && (
            <Field label="May hold">
              <div className="flex flex-wrap items-center gap-1.5">
                {FIATS.map((code) => {
                  const on = ownAssets.includes(code);
                  return (
                    <Chip
                      key={code}
                      active={on}
                      onClick={() => setOwnAssets(on ? ownAssets.filter((x) => x !== code) : [...ownAssets, code])}
                    >
                      {displayAsset(code)}
                    </Chip>
                  );
                })}
              </div>
              {ownAssets.length === 0 && (
                <ErrorLine>Pick at least one — it takes no postings otherwise.</ErrorLine>
              )}
            </Field>
          )}

          <Field label="Provider / institution">
            <input
              className="input"
              value={ownProvider}
              onChange={(e) => setOwnProvider(e.target.value)}
              placeholder={
                ownKind === 'bank'      ? 'ENBD / Wio / ADCB' :
                ownKind === 'exchange'  ? 'Binance / Bitget / OKX' :
                ownKind === 'hot'       ? 'TRC / ERC / BEP' :
                ownKind === 'cold'      ? 'Ledger / Trezor' :
                ownKind === 'cash'      ? 'DXB / Burjuman / Office' :
                                          'PR / merchant name'
              }
            />
          </Field>

          <Field label="Label (optional)">
            <input
              className="input"
              value={ownLabel}
              onChange={(e) => setOwnLabel(e.target.value)}
              placeholder={
                ownKind === 'bank'      ? 'NK / Loki / Main' :
                ownKind === 'exchange'  ? 'Anil / Esub / DXB' :
                ownKind === 'hot'       ? 'V8 / 06' :
                ownKind === 'cold'      ? 'My / Loki' :
                ownKind === 'cash'      ? 'EUR / USD (currency variant)' :
                                          'Main / V2'
              }
            />
          </Field>

          {/* The readback: one line, the name in the party face (.who) —
              it is the display name the party will carry, not an identifier. */}
          {ownSlugAuto && (
            <div className="card-tight text-body">
              <Row label="Will create as" value={<span className="who">{ownDisplayAuto}</span>} />
            </div>
          )}

          <Field label="Parent group (optional)">
            <Combobox
              value={parentSlug}
              onChange={setParentSlug}
              options={parentOptions}
              // "No groups yet" and "Couldn't read groups" are said once, by the line below — never in the box as well.
              placeholder="Pick group — Anil / Bhargav / Sai"
              emptyHint={groups === 'failed' ? 'Groups could not be read.' : 'No such group.'}
              loading={existingGroups.isFetching || groups === 'waiting'}
            />
            {groups === 'failed' ? (
              <StateLine
                tone="warn"
                className="mt-1"
                more={readWords(existingGroups.error)}
                action={<button type="button" className="btn-link text-meta" onClick={() => existingGroups.refetch()}>Retry</button>}
              >
                Couldn&rsquo;t read groups — try again.
              </StateLine>
            ) : groups === 'waiting' ? (
              <p className="text-meta text-tg-hint mt-1">Reading groups…</p>
            ) : parentOptions.length === 0 && (
              <p className="text-meta text-tg-hint mt-1">
                No groups yet ·{' '}
                <button type="button" className="btn-link text-meta" onClick={() => setOwnSubtype('group')}>Create a group</button>
              </p>
            )}
          </Field>
        </>
      )}

      <MoreDetails label="Notes">
        <textarea className="input h-16" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </MoreDetails>

      {/* The refusal above the button, where every entry form keeps it (Book
          trade, Record money) — read before the next tap, not below the last
          thing on the screen. */}
      {submit.isError && <ErrorLine className="mt-1 text-body">{errorWords(submit.error)}</ErrorLine>}

      {/* The phone: full width at the bottom. lg+: hairline separator and a
          right-aligned, content-sized submit — the seat every form gives it. */}
      <div className="lg:flex lg:justify-end lg:border-t lg:border-stroke-soft lg:pt-5">
        <button
          className="btn-primary w-full lg:w-auto lg:px-6"
          disabled={!canSubmit}
          onClick={() => submit.mutate()}
        >
          {submit.isPending ? 'Creating…' : 'Create party'}
        </button>
      </div>
    </div>
  );
}

/** The readback: the label muted at the left, the value at the right. One
 *  line for every normal name; a rare long one wraps rather than clips, so
 *  the operator can always read the whole name they are about to create. */
function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between items-baseline gap-2">
      <span className="text-meta text-tg-hint shrink-0">{label}</span>
      <span className="min-w-0 break-words text-right">{value}</span>
    </div>
  );
}
